// The generated sprites.
//
// A figure ships as a 3x raster and is reduced to its on-screen size **once,
// at load**, into an offscreen canvas — never on the way to the screen.
// Resampling a 66x90 source down to 22x30 sixty times a second produces a
// slightly different set of pixels every frame, which is where the mush comes
// from. Baked once and blitted 1:1, a sprite is the same pixels every frame.

import type { SpriteManifest, SpriteManifestEntry } from "../../content/art-jobs";

export interface Sprite {
  entry: SpriteManifestEntry;
  /** The sheet reduced to its on-screen size, and the same thing mirrored. */
  baked: HTMLCanvasElement;
  mirrored: HTMLCanvasElement;
}

const sprites = new Map<string, Sprite>();

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`could not load ${src}`));
    img.src = src;
  });
}

function bake(img: HTMLImageElement, w: number, h: number, flip: boolean): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  if (flip) {
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
  }
  ctx.drawImage(img, 0, 0, w, h);
  return canvas;
}

/** Load the manifest and bake everything in it. Called once, before the first
 *  frame: a game that pops its art in one sprite at a time looks broken. */
export async function loadSprites(): Promise<void> {
  const res = await fetch("/sprites/manifest.json");
  if (!res.ok) throw new Error(`could not load sprite manifest (${res.status})`);
  const { sprites: entries } = (await res.json()) as SpriteManifest;
  await Promise.all(
    entries.map(async (entry) => {
      const img = await loadImage(`/sprites/${entry.file}`);
      sprites.set(entry.id, {
        entry,
        baked: bake(img, entry.w, entry.h, false),
        mirrored: bake(img, entry.w, entry.h, true),
      });
    }),
  );
}

export const sprite = (id: string): Sprite | undefined => sprites.get(id);

/** Whole-body motion from a distance phase.
 *
 *  Integer offsets are essential at this size: a moving body alternates a
 *  one-pixel sideways jiggle with a one-pixel lift, and an idle one breathes
 *  once in four beats. Nothing here may separate, shear, rotate or scale the
 *  source — the flicker that comes from redrawing a sprite at four sizes a
 *  second reads as the animation being broken rather than as a body moving. */
export function bodyBob(phase: number, moving: boolean): { x: number; y: number } {
  const beat = Math.floor((phase - Math.floor(phase)) * 4) & 3;
  if (!moving) return beat === 2 ? { x: 0, y: -1 } : { x: 0, y: 0 };
  if (beat === 0) return { x: -1, y: 0 };
  if (beat === 1) return { x: 0, y: -1 };
  if (beat === 2) return { x: 1, y: 0 };
  return { x: 0, y: -1 };
}

/** Draw a body with its pivot on (x, y): its feet for a standing figure, its
 *  middle for anything else. `facing` mirrors the cutout — the sources are all
 *  drawn facing the viewer, so this is only ever left/right. */
export function drawBody(
  ctx: CanvasRenderingContext2D,
  id: string,
  x: number,
  y: number,
  facing: "left" | "right" = "right",
  bob: { x: number; y: number } = { x: 0, y: 0 },
): void {
  const found = sprite(id);
  if (!found) return;
  const { w, h, anchor } = found.entry;
  const left = Math.round(x - w / 2 + bob.x);
  const top = Math.round((anchor === "feet" ? y - h : y - h / 2) + bob.y);
  ctx.drawImage(facing === "left" ? found.mirrored : found.baked, left, top);
}
